import { createSlice } from "@reduxjs/toolkit";
import { SubmissionStatus, TemplateType, TemplateUrls } from "services/DataImportService";
import { RootState } from "setup";
import { Step, SubmissionModel } from "../shared/submissionModel";
import {
  closeSubmission,
  createSubmission,
  getSubmissions,
  getTemplateUrls,
  submitSubmission,
  updateSubmissionStep,
} from "../shared/submissionThunks";

type SubmissionsListState = {
  Submissions: SubmissionModel[];
  LoadingSubmissions: boolean;
  ClosingSubmissionID: string | null;
  TemplateUrls: TemplateUrls | null;
  LoadingTemplateUrls: boolean;
};

const initialState: SubmissionsListState = {
  Submissions: [],
  LoadingSubmissions: false,
  ClosingSubmissionID: null,
  TemplateUrls: null,
  LoadingTemplateUrls: false,
};

function applySubmissionUpdate(state: SubmissionsListState, submission: SubmissionModel) {
  const index = state.Submissions.findIndex((item) => item.submissionID === submission.submissionID);
  if (index >= 0) {
    state.Submissions[index] = submission;
  }
}

export const submissionsListSlice = createSlice({
  name: "dataImportSubmissionsList",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getSubmissions.pending, (state) => {
        state.LoadingSubmissions = true;
      })
      .addCase(getSubmissions.fulfilled, (state, action) => {
        state.Submissions = action.payload;
        state.LoadingSubmissions = false;
      })
      .addCase(getSubmissions.rejected, (state) => {
        state.LoadingSubmissions = false;
      })
      .addCase(createSubmission.fulfilled, (state, action) => {
        state.Submissions.unshift(action.payload);
      })
      .addCase(updateSubmissionStep.fulfilled, (state, action) => {
        applySubmissionUpdate(state, action.payload);
      })
      .addCase(submitSubmission.fulfilled, (state, action) => {
        applySubmissionUpdate(state, action.payload);
      })
      .addCase(closeSubmission.pending, (state, action) => {
        state.ClosingSubmissionID = action.meta.arg;
      })
      .addCase(closeSubmission.fulfilled, (state, action) => {
        state.ClosingSubmissionID = null;
        applySubmissionUpdate(state, action.payload);
      })
      .addCase(closeSubmission.rejected, (state) => {
        state.ClosingSubmissionID = null;
      })
      .addCase(getTemplateUrls.pending, (state) => {
        state.LoadingTemplateUrls = true;
      })
      .addCase(getTemplateUrls.fulfilled, (state, action) => {
        state.TemplateUrls = action.payload;
        state.LoadingTemplateUrls = false;
      })
      .addCase(getTemplateUrls.rejected, (state) => {
        state.LoadingTemplateUrls = false;
      });
  },
});

const isPastSubmission = (item: SubmissionModel) =>
  item.submissionStatus === SubmissionStatus.Submitted ||
  item.submissionStatus === SubmissionStatus.Closed ||
  item.currentStep >= Step.Done;

export const selectInProgressSubmissions = (state: RootState) =>
  state.dataImportSubmissionsList.Submissions.filter((item) => !isPastSubmission(item));

export const selectPastSubmissions = (state: RootState) =>
  state.dataImportSubmissionsList.Submissions.filter(isPastSubmission);

export const selectClosingSubmissionID = (state: RootState) => state.dataImportSubmissionsList.ClosingSubmissionID;

export const selectLoadingSubmissions = (state: RootState) => state.dataImportSubmissionsList.LoadingSubmissions;

export const selectTemplateUrls = (state: RootState) => state.dataImportSubmissionsList.TemplateUrls;

// Sorted explicitly rather than relying on GetSubmissions ordering.
export const latestSubmittedStaticData = (submissions: SubmissionModel[]): SubmissionModel | undefined =>
  [...submissions]
    .filter(
      (item) => item.templateType === TemplateType.StaticData && item.submissionStatus === SubmissionStatus.Submitted
    )
    .sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime())[0];

export const selectLatestSubmittedStaticData = (state: RootState) =>
  latestSubmittedStaticData(state.dataImportSubmissionsList.Submissions);

export const balanceImportSubmittedFor = (submissions: SubmissionModel[], staticSubmissionID: string): boolean =>
  submissions.some(
    (item) =>
      item.templateType === TemplateType.BalanceImport &&
      item.submissionStatus === SubmissionStatus.Submitted &&
      item.parentSubmissionID === staticSubmissionID
  );

export const selectBalanceImportSubmittedForLatestStatic = (state: RootState) => {
  const latest = latestSubmittedStaticData(state.dataImportSubmissionsList.Submissions);
  return (
    latest !== undefined && balanceImportSubmittedFor(state.dataImportSubmissionsList.Submissions, latest.submissionID)
  );
};

export default submissionsListSlice.reducer;
